import React from "react";
import { Conflict } from "@opstel/query/v2/models/conflict";
import { DATETIME_FORMAT_NO_DATE_NO_SECONDS } from "@shared/common/utils/luxonHelpers";
import { useSelectedTimeStore } from "@shared/common/contexts/SelectedTime/useSelectedTimeStore";
import classNames from "classnames";

interface ConflictItemProps {
  readonly conflict: Conflict;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
}

export function ConflictItem({
  conflict,
  isExpanded,
  onToggle,
}: ConflictItemProps): React.JSX.Element {
  const setDateTime = useSelectedTimeStore((state) => state.setDateTime);

  const handleToggle = () => {
    if (!isExpanded) {
      setDateTime(conflict.timeInterval.start);
    }
    onToggle();
  };

  return (
    <>
      <button
        onClick={handleToggle}
        aria-expanded={isExpanded}
        className={classNames(
          "w-full appearance-none border-0 bg-transparent p-0 text-left",
          "grid grid-cols-[1fr_auto_auto_24px] items-center gap-xs",
          "min-h-[35px] border-b select-none",
          "ease-in-out transition-colors duration-200",
          "hover:cursor-context-menu",
          "focus:bg-info-subtle focus:outline-none",
          isExpanded
            ? "border-transparent bg-info-subtle hover:bg-info-subtle"
            : "border-subtle bg-transparent hover:bg-hover",
        )}
      >
        <span
          className="col-start-1 overflow-hidden pl-spacer-1 text-s leading-l font-normal text-ellipsis whitespace-nowrap text-content"
          title={conflict.type}
        >
          {conflict.type}
        </span>
        <span className="col-start-3 min-w-[54px] text-right text-xs leading-l font-normal text-subtle">
          {conflict.timeInterval.start.toFormat(DATETIME_FORMAT_NO_DATE_NO_SECONDS)}
        </span>
        <span
          className={classNames(
            "col-start-4 flex justify-center text-interactive",
            "ease-in-out transition-transform duration-200",
            isExpanded ? "rotate-180" : "rotate-0",
          )}
        >
          <tractie-icon name="chevron-down" />
        </span>
      </button>
      <div
        className={classNames(
          "rounded-b relative overflow-y-hidden bg-info-subtle px-xs",
          "[transition:max-height_0.3s_ease-out,opacity_0.2s_ease-out,padding_0.3s_ease-out]",
          isExpanded ? "h-auto max-h-screen pt-0 pb-m opacity-100" : "h-0 max-h-0 py-0 opacity-0",
        )}
      >
        <p className="m-0 mb-xs text-s leading-l font-normal text-subtle">
          {conflict.displayDescription}
        </p>
        {conflict.detectedBy && (
          <tractie-tag appearance="subtle" color="gray">
            {conflict.detectedBy}
          </tractie-tag>
        )}
      </div>
    </>
  );
}
